import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import {
  PageHeader,
  Loader,
  KpiCard,
  EmptyState,
} from "@/components/common/Bits";
import { cn } from "@/lib/utils";
import {
  Building2,
  GitBranch,
  IndianRupee,
  ShieldAlert,
  Pencil,
  Plus,
  Search,
  Users,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
const PLAN = {
  basic: "bg-amber-100 text-amber-700",
  professional: "bg-orange-100 text-orange-700",
  advanced: "bg-violet-100 text-violet-700",
};

const RestaurantDialog = ({
  mode,
  open,
  restaurant,
  form,
  setForm,
  plans,
  onClose,
  onSaved,
}) => {
  const [busy, setBusy] = useState(false);
  const subscription = restaurant?.subscription || {};

  const [renewalBusy, setRenewalBusy] = useState(false);

  const handleSubscriptionAction = async (action) => {
    if (!restaurant?.id || renewalBusy) return;

    setRenewalBusy(true);

    try {
      if (action === "renewal") {
        const response = await api.createAdminRestaurantRenewal(restaurant.id);

        const shortUrl = response.data?.short_url;
        if (!shortUrl) {
          throw new Error("Renewal payment link was not returned");
        }

        toast.success("Renewal link created successfully");
        await onSaved();
        onClose();
        window.open(shortUrl, "_blank", "noopener,noreferrer");
        return;
      }

      if (action === "cancelled") {
        await api.updateAdminRestaurant(restaurant.id, {
          action: "cancel_subscription",
        });

        toast.success("Subscription cancelled successfully");
        await onSaved();
        onClose();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          error.message ||
          "Subscription action failed",
      );
    } finally {
      setRenewalBusy(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const change = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const loadRazorpay = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;

      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Razorpay SDK failed to load"));

      document.body.appendChild(script);
    });

  const startTrialPayment = async (restaurantId) => {
    const { data } = await api.createSubscription(restaurantId);

    await loadRazorpay();

    return new Promise((resolve, reject) => {
      let settled = false;

      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        fn(value);
      };

      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,

        name: "StayServe",
        description: "₹99 Trial + 7 Days Free",

        handler: async (response) => {
          try {
            await api.verifyTrialSubscription(restaurantId, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            });

            toast.success("₹99 payment successful. 7-day trial activated.");

            finish(resolve, true);
          } catch (e) {
            toast.error(
              e.response?.data?.detail || "Subscription verification failed",
            );

            finish(reject, e);
          }
        },

        modal: {
          ondismiss: () => {
            toast.info("Trial payment cancelled");

            finish(reject, new Error("Trial payment cancelled"));
          },
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", (response) => {
        const message = response.error?.description || "Trial payment failed";

        toast.error(message);

        finish(reject, new Error(message));
      });

      razorpay.open();
    });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Restaurant name is required");
      return;
    }

    if (mode === "add") {
      if (!form.owner_name.trim()) {
        toast.error("Owner name is required");
        return;
      }

      if (!form.owner_email.trim()) {
        toast.error("Owner email is required");
        return;
      }

      if (!form.password || form.password.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }
    }

    setBusy(true);

    try {
      if (mode === "add") {
        const created = await api.createAdminRestaurant({
          name: form.name.trim(),
          owner_name: form.owner_name.trim(),
          owner_email: form.owner_email.trim(),
          password: form.password,
          phone: form.phone.trim(),
          gstin: form.gstin.trim(),
          plan: form.plan,
        });

        const restaurantId = created.data.restaurant.id;

        // Basic plan = ₹99 + 7-day trial
        if (["starter", "basic"].includes(form.plan)) {
          await startTrialPayment(restaurantId);

          await onSaved();
          onClose();

          return;
        }

        // Keep existing behavior for other plans
        toast.success("Restaurant added successfully");
      } else {
        await api.updateAdminRestaurant(restaurant.id, {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          gstin: form.gstin.trim(),
        });

        const action = form.subscriptionAction;

        if (action === "cancelled") {
          await api.updateAdminRestaurant(restaurant.id, {
            action: "cancel_subscription",
          });

          toast.success("Subscription cancellation processed");
        } else if (action === "renewal") {
          const response = await api.createAdminRestaurantRenewal(
            restaurant.id,
          );

          const shortUrl = response.data?.short_url;

          if (!shortUrl) {s
            throw new Error("Renewal payment link was not returned");
          }

          toast.success("Renewal link created successfully");

          await onSaved();
          onClose();

          window.open(shortUrl, "_blank", "noopener,noreferrer");
          return;
        } else {
          toast.success("Restaurant updated successfully");
        }
      }

      await onSaved();
      onClose();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          `Failed to ${mode === "add" ? "add" : "update"} restaurant`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {mode === "add" ? "Add Restaurant" : "Edit Restaurant"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            value={form.name}
            onChange={(e) => change("name", e.target.value)}
            placeholder="Restaurant name"
          />
          {mode === "edit" && (
            <>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => change("email", e.target.value)}
                placeholder="Restaurant email"
                autoComplete="email"
              />

              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => change("phone", e.target.value)}
                placeholder="Restaurant phone"
                autoComplete="tel"
              />
            </>
          )}
          {mode === "add" && (
            <>
              <Input
                value={form.owner_name}
                onChange={(e) => change("owner_name", e.target.value)}
                placeholder="Owner name"
              />

              <Input
                type="email"
                value={form.owner_email}
                onChange={(e) => change("owner_email", e.target.value)}
                placeholder="Owner email"
                autoComplete="email"
              />

              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => change("phone", e.target.value)}
                placeholder="Owner phone"
                autoComplete="tel"
              />

              <Input
                type="password"
                value={form.password}
                onChange={(e) => change("password", e.target.value)}
                placeholder="Owner login password"
                autoComplete="new-password"
              />
            </>
          )}
          {mode === "add" && (
            <select
              value={form.plan}
              onChange={(e) => change("plan", e.target.value)}
              className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
            >
              {plans.map((plan) => (
                <option key={plan.id} value={String(plan.name).toLowerCase()}>
                  {plan.name} — ₹{Number(plan.price).toLocaleString("en-IN")}/
                  {plan.cycle || "month"}
                </option>
              ))}
            </select>
          )}

          {mode === "edit" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Subscription Action</label>

              <select
                value={form.subscriptionAction || ""}
                disabled={busy || renewalBusy}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    subscriptionAction: e.target.value,
                  }));
                }}
                className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm disabled:opacity-60"
              >
                <option value="">Select action</option>
                <option value="cancelled">Cancelled</option>
                <option value="renewal">Renewal</option>
              </select>

              {renewalBusy && (
                <p className="text-xs text-muted-foreground">
                  Processing request...
                </p>
              )}
            </div>
          )}

          <Input
            value={form.gstin}
            onChange={(e) => change("gstin", e.target.value)}
            placeholder="GSTIN"
          />
        </div>
        {mode === "edit" && restaurant && (
          <div className="rounded-xl border border-border bg-secondary/30 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Subscription Details</h4>

              <span className="rounded-full bg-amber-100 text-amber-700 px-2.5 py-0.5 text-xs font-semibold capitalize">
                {subscription.status || "trial"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Current Plan</p>
                <p className="font-semibold capitalize">
                  {subscription.plan || restaurant.plan || "—"}
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground">Started</p>
                <p className="font-semibold">
                  {formatDate(subscription.started)}
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground">Trial Amount</p>
                <p className="font-semibold">
                  ₹
                  {Number(subscription.trial_amount || 99).toLocaleString(
                    "en-IN",
                  )}
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground">Trial Period</p>
                <p className="font-semibold">
                  {subscription.trial_days || 7} days
                </p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground">
                  Recurring Amount
                </p>
                <p className="font-semibold">
                  ₹
                  {Number(
                    subscription.recurring_amount ??
                      subscription.next_billing_amount ??
                      0,
                  ).toLocaleString("en-IN")}
                  /month
                </p>
              </div>
            </div>

            <div className="pt-2 border-t border-border text-xs text-muted-foreground">
              <p>
                ₹
                {Number(subscription.trial_amount ?? 99).toLocaleString(
                  "en-IN",
                )}
                payment → {subscription.trial_days || 7}-day trial → thereafter
                ₹
                {Number(
                  subscription.recurring_amount ??
                    subscription.next_billing_amount ??
                    0,
                ).toLocaleString("en-IN")}
                /month
              </p>

              {subscription.trial_payment_status && (
                <p className="mt-1">
                  Trial payment status:{" "}
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${
                      subscription.status === "active"
                        ? "bg-green-100 text-green-700"
                        : subscription.status === "failed" ||
                            subscription.status === "payment_failed"
                          ? "bg-red-100 text-red-700"
                          : subscription.status === "cancelled"
                            ? "bg-gray-200 text-gray-700"
                            : subscription.status === "paused"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {subscription.status === "payment_failed"
                      ? "Payment Failed"
                      : subscription.status || "Trial"}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={save}
            disabled={busy}
            className="w-full h-11 rounded-xl font-semibold"
          >
            {busy
              ? "Saving..."
              : mode === "add"
                ? "Create Restaurant"
                : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function SuperAdmin() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [restaurantDialog, setRestaurantDialog] = useState(false);
  const [restaurantForm, setRestaurantForm] = useState({
    name: "",
    owner_name: "",
    owner_email: "",
    email: "",
    password: "",
    phone: "",
    plan: "starter",
    status: "active",
    gstin: "",
  });
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantStatusFilter, setRestaurantStatusFilter] = useState("all");
  const [restaurantPlanFilter, setRestaurantPlanFilter] = useState("all");
  const [restaurantPage, setRestaurantPage] = useState(1);
  const RESTAURANTS_PER_PAGE = 10;
  const [salesPartners, setSalesPartners] = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerDialog, setPartnerDialog] = useState(false);
  const [partnerForm, setPartnerForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  useEffect(() => {
    api
      .adminOverview()
      .then((r) => setD(r.data))
      .catch(() => setErr(true));
  }, []);

  const restaurants = d?.restaurants || [];

  const filteredRestaurants = restaurants.filter((r) => {
    const search = restaurantSearch.trim().toLowerCase();
    const matchesSearch =
      !search ||
      String(r.name || "")
        .toLowerCase()
        .includes(search) ||
      String(r.owner_name || "")
        .toLowerCase()
        .includes(search) ||
      String(r.email || "")
        .toLowerCase()
        .includes(search) ||
      String(r.phone || "")
        .toLowerCase()
        .includes(search) ||
      String(r.owner_email || "")
        .toLowerCase()
        .includes(search) ||
      String(r.gstin || "")
        .toLowerCase()
        .includes(search);

    const restaurantStatus = String(r.status || "active").toLowerCase();
    const matchesStatus =
      restaurantStatusFilter === "all" ||
      restaurantStatus === restaurantStatusFilter;

    const subscription = (d?.subscriptions || []).find(
      (s) => s.restaurant_id === r.id,
    );
    const plan = String(subscription?.plan || r.plan || "basic").toLowerCase();
    const matchesPlan =
      restaurantPlanFilter === "all" || plan === restaurantPlanFilter;

    return matchesSearch && matchesStatus && matchesPlan;
  });

  const restaurantTotalPages = Math.max(
    1,
    Math.ceil(filteredRestaurants.length / RESTAURANTS_PER_PAGE),
  );

  const safeRestaurantPage = Math.min(restaurantPage, restaurantTotalPages);
  const restaurantStartIndex = (safeRestaurantPage - 1) * RESTAURANTS_PER_PAGE;
  const paginatedRestaurants = filteredRestaurants.slice(
    restaurantStartIndex,
    restaurantStartIndex + RESTAURANTS_PER_PAGE,
  );
  const restaurantStart =
    filteredRestaurants.length === 0 ? 0 : restaurantStartIndex + 1;
  const restaurantEnd = Math.min(
    restaurantStartIndex + RESTAURANTS_PER_PAGE,
    filteredRestaurants.length,
  );

  useEffect(() => {
    if (restaurantPage !== safeRestaurantPage) {
      setRestaurantPage(safeRestaurantPage);
    }
  }, [restaurantPage, safeRestaurantPage]);

  if (err)
    return (
      <div className="p-6">
        <EmptyState
          icon={ShieldAlert}
          title="Super Admin only"
          subtitle="Sign in with the super admin account to view the platform panel."
        />
      </div>
    );
  if (!d)
    return (
      <div className="p-6">
        <Loader label="Loading platform overview" />
      </div>
    );

  return (
    <>
      <div className="p-4 sm:p-6 animate-fade-up">
        <PageHeader
          title="Super Admin"
          subtitle="SaaS platform overview"
          actions={
            <Button
              onClick={() => {
                setRestaurantForm({
                  name: "",
                  owner_name: "",
                  owner_email: "",
                  email: "",
                  password: "",
                  phone: "",
                  gstin: "",
                  plan: "starter",
                  status: "active",
                });
                setRestaurantDialog("add");
              }}
              className="rounded-xl"
            >
              <Plus size={16} />
              Add Restaurant
            </Button>
          }
        />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <KpiCard
            label="Restaurants"
            value={d.total_restaurants}
            icon={Building2}
            accent="orange"
            sub={`${d.active_restaurants} active`}
          />
          <KpiCard
            label="Branches"
            value={d.total_branches}
            icon={GitBranch}
            accent="sky"
          />
          <KpiCard
            label="Platform Revenue"
            value={money(d.platform_revenue)}
            icon={IndianRupee}
            accent="emerald"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl bg-card border border-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border space-y-3">
              <div>
                <h3 className="font-display font-semibold">Restaurants</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {filteredRestaurants.length}{" "}
                  {filteredRestaurants.length === 1
                    ? "restaurant"
                    : "restaurants"}
                </p>
              </div>

              <div className="flex flex-col lg:flex-row gap-2">
                <div className="relative flex-1">
                  <Search
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    value={restaurantSearch}
                    onChange={(e) => {
                      setRestaurantSearch(e.target.value);
                      setRestaurantPage(1);
                    }}
                    placeholder="Search restaurant, owner, email or GSTIN"
                    className="h-10 pl-9 rounded-xl"
                  />
                </div>
                <select
                  value={restaurantStatusFilter}
                  onChange={(e) => {
                    setRestaurantStatusFilter(e.target.value);
                    setRestaurantPage(1);
                  }}
                  className="h-10 rounded-xl border border-border bg-white px-3 text-sm lg:w-40"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select
                  value={restaurantPlanFilter}
                  onChange={(e) => {
                    setRestaurantPlanFilter(e.target.value);
                    setRestaurantPage(1);
                  }}
                  className="h-10 rounded-xl border border-border bg-white px-3 text-sm lg:w-44"
                >
                  <option value="all">All Plans</option>
                  <option value="starter">Starter</option>
                  <option value="basic">Basic</option>
                  <option value="professional">Professional</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[1350px] text-sm">
                <thead className="bg-secondary/50 text-muted-foreground">
                  <tr className="text-left">
                    {[
                      "Restaurant",
                      "Email",
                      "Phone",
                      "Plan",
                      "Branches",
                      "Subscription",
                      "Restaurant Status",
                      "GSTIN",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-5 py-3 text-xs font-semibold uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paginatedRestaurants.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-5 py-12 text-center text-sm text-muted-foreground"
                      >
                        No restaurants found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    paginatedRestaurants.map((r) => {
                      const sub = (d.subscriptions || []).find(
                        (s) => s.restaurant_id === r.id,
                      );

                      // ---------------------------------------------------
                      // PLAN
                      // Trial is NOT a plan.
                      // Example:
                      // Plan = Basic
                      // Subscription = Trial
                      // ---------------------------------------------------
                      const plan = (
                        sub?.plan ||
                        r.plan ||
                        "basic"
                      ).toLowerCase();

                      const planName =
                        plan === "starter"
                          ? "Starter"
                          : plan === "basic"
                            ? "Basic"
                            : plan === "professional"
                              ? "Professional"
                              : plan === "advanced"
                                ? "Advanced"
                                : plan;

                      // ---------------------------------------------------
                      // SUBSCRIPTION STATUS
                      // ---------------------------------------------------
                      const subscriptionStatus = (
                        sub?.status || "trial"
                      ).toLowerCase();

                      const restaurantStatus = (
                        r.status || "active"
                      ).toLowerCase();

                      const restaurantStatusClass =
                        restaurantStatus === "active"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-700";

                      const restaurantStatusLabel =
                        restaurantStatus === "active" ? "Active" : "Cancelled";
                      const statusClass =
                        subscriptionStatus === "active"
                          ? "bg-emerald-100 text-emerald-700"
                          : subscriptionStatus === "failed" ||
                              subscriptionStatus === "payment_failed"
                            ? "bg-red-100 text-red-700"
                            : subscriptionStatus === "cancelled"
                              ? "bg-gray-200 text-gray-700"
                              : subscriptionStatus === "paused"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-amber-100 text-amber-700";

                      const statusLabel =
                        subscriptionStatus === "payment_failed"
                          ? "Payment Failed"
                          : subscriptionStatus === "cancelled"
                            ? "Cancelled"
                            : subscriptionStatus === "paused"
                              ? "Paused"
                              : subscriptionStatus === "active"
                                ? "Active"
                                : "Trial";

                      // ---------------------------------------------------
                      // BILLING AMOUNT
                      // ---------------------------------------------------
                      const planAmount = Number(
                        sub?.recurring_amount ||
                          sub?.next_billing_amount ||
                          sub?.amount ||
                          0,
                      );

                      // ---------------------------------------------------
                      // BRANCH COUNT
                      // ---------------------------------------------------
                      const branchCount = (d.branches || []).filter(
                        (b) => b.restaurant_id === r.id,
                      ).length;

                      // ---------------------------------------------------
                      // DATES
                      // ---------------------------------------------------
                      const formatDate = (value) => {
                        if (!value) return null;

                        const date = new Date(value);

                        if (Number.isNaN(date.getTime())) {
                          return null;
                        }

                        return date.toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        });
                      };

                      const trialEnd = sub?.trial_end;
                      const nextBillingDate = sub?.next_billing_date;

                      const trialDaysRemaining = sub?.trial_days_remaining;

                      return (
                        <tr
                          key={r.id}
                          className="border-t border-border hover:bg-secondary/30"
                        >
                          {/* ---------------------------------------------
              RESTAURANT
          ---------------------------------------------- */}
                          <td className="px-5 py-4 font-semibold">{r.name}</td>

                          <td className="px-5 py-4 text-muted-foreground">
                            {r.email || "—"}
                          </td>

                          <td className="px-5 py-4 text-muted-foreground">
                            {r.phone || "—"}
                          </td>

                          {/* ---------------------------------------------
              PLAN
          ---------------------------------------------- */}
                          <td className="px-5 py-4">
                            <span
                              className={cn(
                                "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                                PLAN[plan] || "bg-stone-100 text-stone-700",
                              )}
                            >
                              {planName}
                            </span>
                          </td>

                          {/* ---------------------------------------------
              BRANCHES
          ---------------------------------------------- */}
                          <td className="px-5 py-4">
                            <span className="font-semibold">{branchCount}</span>
                          </td>

                          {/* ---------------------------------------------
              SUBSCRIPTION
          ---------------------------------------------- */}
                          <td className="px-5 py-4">
                            <div className="flex flex-col gap-1.5">
                              {/* Status */}
                              <div>
                                <span
                                  className={cn(
                                    "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                                    statusClass,
                                  )}
                                >
                                  {statusLabel}
                                </span>
                              </div>

                              {/* Billing Amount */}
                              {planAmount > 0 && (
                                <div className="text-sm font-semibold">
                                  ₹{planAmount.toLocaleString("en-IN")}
                                  <span className="font-normal text-muted-foreground">
                                    /month
                                  </span>
                                </div>
                              )}

                              {/* Trial Information */}
                              {subscriptionStatus === "trial" && (
                                <div className="text-xs text-muted-foreground space-y-0.5">
                                  {trialDaysRemaining !== null &&
                                    trialDaysRemaining !== undefined && (
                                      <div>
                                        {trialDaysRemaining}{" "}
                                        {trialDaysRemaining === 1
                                          ? "day"
                                          : "days"}{" "}
                                        remaining
                                      </div>
                                    )}

                                  {trialEnd && (
                                    <div>
                                      Trial ends: {formatDate(trialEnd)}
                                    </div>
                                  )}

                                  <div>
                                    Next billing:{" "}
                                    {formatDate(nextBillingDate || trialEnd) ||
                                      "—"}
                                  </div>

                                  {sub?.next_billing_amount && (
                                    <div>
                                      Next billing amount: ₹
                                      {Number(
                                        sub.next_billing_amount,
                                      ).toLocaleString("en-IN")}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Active Subscription */}
                              {subscriptionStatus === "active" && (
                                <div className="text-xs text-muted-foreground">
                                  {nextBillingDate ? (
                                    <>
                                      Next billing:{" "}
                                      {formatDate(nextBillingDate)}
                                    </>
                                  ) : (
                                    "Monthly billing"
                                  )}
                                </div>
                              )}

                              {/* Failed Payment */}
                              {(subscriptionStatus === "failed" ||
                                subscriptionStatus === "payment_failed") && (
                                <div className="text-xs text-red-600 font-medium">
                                  Payment requires attention
                                </div>
                              )}

                              {/* Cancelled */}
                              {subscriptionStatus === "cancelled" && (
                                <div className="text-xs text-muted-foreground">
                                  Subscription cancelled
                                </div>
                              )}

                              {/* Paused */}
                              {subscriptionStatus === "paused" && (
                                <div className="text-xs text-muted-foreground">
                                  Subscription paused
                                </div>
                              )}
                            </div>
                          </td>
                          {/* ---------------------------------------------
    RESTAURANT STATUS
---------------------------------------------- */}
                          <td className="px-5 py-4">
                            <span
                              className={cn(
                                "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                                restaurantStatusClass,
                              )}
                            >
                              {restaurantStatusLabel}
                            </span>
                          </td>
                          {/* ---------------------------------------------
              GSTIN
          ---------------------------------------------- */}
                          <td className="px-5 py-4 text-muted-foreground">
                            {r.gstin || "—"}
                          </td>

                          {/* ---------------------------------------------
              ACTIONS
          ---------------------------------------------- */}

                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingRestaurant(r);

                                  setRestaurantForm({
                                    name: r.name || "",
                                    email: r.email || "",
                                    phone: r.phone || "",
                                    status: r.status || "active",
                                    gstin: r.gstin || "",
                                  });

                                  setRestaurantDialog("edit");
                                }}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-secondary"
                                title="Edit restaurant"
                              >
                                <Pencil size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {filteredRestaurants.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-border px-5 py-3">
                <div className="text-xs text-muted-foreground whitespace-nowrap">
                  Showing{" "}
                  <span className="font-semibold text-foreground">
                    {restaurantStart}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-foreground">
                    {restaurantEnd}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-foreground">
                    {filteredRestaurants.length}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setRestaurantPage((p) => Math.max(1, p - 1))}
                    disabled={safeRestaurantPage === 1}
                    className="h-8 rounded-lg border border-border bg-white px-3 text-xs font-semibold hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Previous
                  </button>
                  {Array.from(
                    { length: restaurantTotalPages },
                    (_, i) => i + 1,
                  ).map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      onClick={() => setRestaurantPage(pageNumber)}
                      className={
                        safeRestaurantPage === pageNumber
                          ? "grid h-8 min-w-8 place-items-center rounded-lg bg-primary px-2 text-xs font-semibold text-white"
                          : "grid h-8 min-w-8 place-items-center rounded-lg border border-border bg-white px-2 text-xs font-semibold text-foreground hover:bg-secondary"
                      }
                    >
                      {pageNumber}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setRestaurantPage((p) =>
                        Math.min(restaurantTotalPages, p + 1),
                      )
                    }
                    disabled={safeRestaurantPage === restaurantTotalPages}
                    className="h-8 rounded-lg border border-border bg-white px-3 text-xs font-semibold hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-card border border-border p-5">
            <h3 className="font-display font-semibold mb-4">System Health</h3>
            <div className="space-y-3">
              {Object.entries(d.system_health).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="capitalize text-sm font-medium">{k}</span>
                  <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" /> {v}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                Subscription Plans
              </p>
              {(d.plans || []).map((plan) => (
                <div
                  key={plan.id}
                  className="flex items-center justify-between py-1.5 text-sm"
                >
                  <span className="font-medium">{plan.name}</span>
                  <span className="text-muted-foreground">
                    ₹{Number(plan.price).toLocaleString("en-IN")}/
                    {plan.cycle || "month"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <RestaurantDialog
        open={!!restaurantDialog}
        restaurant={editingRestaurant}
        form={restaurantForm}
        setForm={setRestaurantForm}
        plans={d?.plans || []}
        mode={restaurantDialog}
        onClose={() => setRestaurantDialog(false)}
        onSaved={async () => {
          const r = await api.adminOverview();
          setD(r.data);
        }}
      />
    </>
  );
}
